import { FileType } from '@/components/Icon';
import { ShortcutKey } from './typings';

/** 最宽松，用于接收短信 */
export const phoneReg = /^(?:(?:\+|00)86)?1\d{10}$/;

export const isPhone = (value?: string) => value && phoneReg.test(value);

export const getFileType = (fileName: string) => {
  return fileName?.split('.').pop() as FileType;
};

export function getAccpetFileTypes(accept: string): string[] {
  return accept.split(',').map((item) => item.replace('.', ''));
}

export const tryCatch = async (promise: () => Promise<void>) => {
  try {
    await promise();
  } catch (e) {}
};

/**
 * 解析json不报错
 * @param json
 * @param fallback 字符串为空或解析失败时返回的值
 * @returns
 */
export function safeParseJson<T>(json: string | null, fallback: T): T {
  if (!json) {
    return fallback;
  }
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

/**
 * 根据文件地址下载文件
 */
export function downloadByUrl(data: {
  url: string;
  target?: string;
  fileName?: string;
}) {
  let { url, target = '_blank', fileName } = data;
  const isChrome =
    window.navigator.userAgent.toLowerCase().indexOf('chrome') > -1;
  const isSafari =
    window.navigator.userAgent.toLowerCase().indexOf('safari') > -1;

  // if (/(iP)/g.test(window.navigator.userAgent)) {
  //   // console.error('您的浏览器不支持下载!');
  //   copyToClipboard(url || '');
  //   message.success('下载链接已复制到剪贴板，请使用默认浏览器打开下载');
  //   // return;
  //   return false;
  // }
  if (isChrome || isSafari) {
    const link = document.createElement('a');
    link.href = url;
    link.target = target;

    if (link.download !== undefined) {
      link.download =
        fileName || url.substring(url.lastIndexOf('/') + 1, url.length);
    }

    if (document.createEvent) {
      const e = document.createEvent('MouseEvents');
      e.initEvent('click', true, true);
      link.dispatchEvent(e);
      return true;
    }
  }
  if (url.indexOf('?') === -1) {
    url += '?download';
  }

  window.open(url, target);
  return true;
}

export function openPdfInNewWindow(url: string) {
  window.open(`/pdf.js/web/viewer.html?file=${encodeURIComponent(url)}`);
}

/** 是否是一个有效的邮箱 */
export function validateEmail(email?: string) {
  if (!email) return false;
  const regExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return regExp.test(email);
}

/** 判断是否是mac */
export function isMacos() {
  const agent = navigator.userAgent.toLowerCase();
  return /macintosh|mac os x/i.test(agent);
}

/** win和mac分别用不同的展示和快捷键 */
export const replaceShortcut = (key: ShortcutKey) => {
  if (isMacos()) {
    return {
      key,
      text: '⌘',
    };
  }
  return {
    key: key.replace('meta', 'ctrl'),
    text: 'Ctrl',
  };
};

/** 一个文件是否是pdf */
export function isPdfFile(file: File) {
  const { type, name } = file;
  const fileType = getFileType(name);
  return type === 'application/pdf' && fileType?.toLowerCase() === 'pdf';
}

export const EXCEL_FILE_ACCEPT = '.xlsx,.xls';

/** 一个文件是否是excel */
export function isExcelFile(file: File) {
  const { name } = file;
  const fileType = getFileType(name);
  return fileType?.toLowerCase()?.includes('xls');
}

export function formatNumberToLocaleString(num?: number | null) {
  if (!num) {
    return 0;
  }
  return num.toLocaleString();
}

export function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|MacWechat|MicroMessenger/i.test(
    navigator.userAgent,
  );
}

export function isWxBrowser() {
  // 判断是否H5微信环境，true为微信浏览器
  return /MicroMessenger/i.test(navigator.userAgent);
}

export function isSuper() {
  return (
    window.location.host.includes('superadmin') ||
    window.location.host.includes('localhost')
  );
}

/** 判断文件是否是 EndNote XML */
export function isEndnoteXMLFile(file: File) {
  const { name } = file;
  const fileType = getFileType(name);
  return fileType?.toLowerCase()?.includes('xml');
}

/**
 * 将十六进制颜色值转换为 RGB 格式
 * @param hex 十六进制颜色值（支持 3 位或 6 位格式，带或不带 # 前缀）
 * @returns RGB 格式字符串（如 "rgb(255, 0, 0)"），若输入无效则返回 null
 */
export function hexToRgb(hex: string): string {
  // 移除 # 前缀（如果存在）
  const formattedHex = hex.replace(/^#/, '');

  // 验证格式有效性（3 位或 6 位十六进制）
  if (!/^([0-9A-F]{3}){1,2}$/i.test(formattedHex)) {
    return '0,0,0';
  }

  // 处理 3 位简写格式
  let fullHex = formattedHex;
  if (formattedHex.length === 3) {
    fullHex = formattedHex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // 解析 RGB 分量
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
}

const REGEXP = /[xy]/g;
const PATTERN = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
function replacement(c: string): string {
  const r = (Math.random() * 16) | 0;
  const v = c === 'x' ? r : (r & 0x3) | 0x8;
  return v.toString(16);
}
export function makeUuid() {
  return PATTERN.replace(REGEXP, replacement);
}

export function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} bytes`;
  } else if (size >= 1024 && size < 1048576) {
    return `${(size / 1024).toFixed(0)} KB`;
  } else if (size >= 1048576) {
    return `${(size / 1048576).toFixed(0)} MB`;
  }
}

export function isEncodedURIComponent(str: string) {
  const regex = /%[0-9A-Fa-f]{2}/;
  return regex.test(str);
}

export function decodeURIComponentFn(str: string) {
  try {
    if (!str || !isEncodedURIComponent(str)) {
      return str;
    }
    const text = decodeURIComponent(str);
    if (isEncodedURIComponent(text)) {
      return decodeURIComponentFn(text);
    }
    return text;
  } catch (err) {
    return str;
  }
}
